import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import he from 'he';

import './textDisplay.css';

// Redux Store
import { adminAnalyzerSelector } from 'store/Admin/Analyser';
import { UserSessionSelector } from 'store/UserSession';

// Components
import { Form } from '@unform/web';
import AnalyseText from 'components/Admin/AnalyseText';
import { CustomEditor } from 'components/shared/CustomEditor';
import { Loader } from 'components/shared/Loader';
import ComparisonModal from 'components/shared/modal/ComparisonModal';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import SavingIndicator from 'components/shared/AutoSaveDisplay';
import { RevisionDetails } from 'components/shared/RevisionDetails';
import { TextDisplaySidebar } from 'components/shared/TextDisplaySidebar/TextDisplaySidebar';
import { DownloadOneTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import { InitialTextSection } from 'components/shared/InitialTextSection';
import { FormHeader } from 'components/shared/TextDisplay/FormHeader';
import { TextDetailsSection } from 'components/shared/TextDisplay/TextDetailsSection';
import { LanguageDropdown } from 'components/shared/TextDisplay/LanguageDropdown';
import { WordCountDisplay } from 'components/shared/TextDisplay/WordCountDisplay';
import { replaceTextInBold, entitiesToSpecialChars } from 'utils/StringUtils';
// Helpers
import { checkDataAdmin } from 'helpers/formHelper';
import { autoSaveText } from 'helpers/CreateAutoSave';
import { fetchAnalysisResult } from 'helpers/fetchAnalysisResultHelper';

// Utils
import {
  ANALYSELANGAUGESLIST,
  ROLES,
  sentenceSeparators,
} from 'utils/Constants';
import { keywordsAutoGeneration } from 'utils/KeywordsAutoGeneration/keywordsAutoGeneration';

// Hooks
import { useAutosave } from 'utils/hooks/useAutoSave';
import { useSidebar } from 'utils/hooks/features/textDisplay/useSidebar';
import { useAdminTextForm } from 'utils/hooks/features/textDisplay/admin/useAdminTextForm';
import { useEditorManagement } from 'utils/hooks/features/textDisplay/useEditorManagement';
import { useWordCountTracker } from 'utils/hooks/features/textDisplay/useWordCountTracker';
import { useTextStatus } from 'utils/hooks/features/textDisplay/useTextStatus';
import { useAdminPutToDraft } from 'utils/hooks/features/textDisplay/admin/useAdminPutToDraft';
import { useAdminRegenerateChatGPTText } from 'utils/hooks/features/textDisplay/admin/useAdminRegenerateChatGPTText';
import { useAdminWriterBilling } from 'utils/hooks/features/textDisplay/admin/useAdminWriterBilling';
import { useAdminInvalidateText } from 'utils/hooks/features/textDisplay/admin/useAdminInvalidateText';
import { useAdminEditText } from 'utils/hooks/features/textDisplay/admin/useAdminEditText';
import { useAdminTextActions } from 'utils/hooks/features/textDisplay/admin/useAdminTextActions';
import { useAdminReactivateInitialText } from 'utils/hooks/features/textDisplay/admin/useAdminReactivateInitialText';
import { useNotifyClientKeywordStuffing } from 'utils/hooks/features/textDisplay/admin/useNotifyClientKeywordStuffing';
import { useAdminTextRegenerationStatus } from 'utils/hooks/features/textDisplay/admin/useAdminTextRegenerationStatus';

import {
  SemanticScoreProvider,
  useSemanticScore,
} from 'utils/contexts/SemanticScoreContext';

import {
  useWordsInBold,
  WordsProvider,
} from 'utils/contexts/ScoreBoldWordsContext';

function TextDisplayContent() {
  const [language, setLanguage] = useState(ANALYSELANGAUGESLIST[0]);

  const [loading, setLoading] = useState(false);
  const [isFetchLoading, setIsFetchLoading] = useState(false);

  const [showComparison, setShowComparison] = useState(false);

  const [disabledForm, setDisabledForm] = useState(true);

  const [keywordStuffingLoading, setKeywordStuffingLoading] = useState(false);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [errorSuccessImg, setErrorSuccessImg] = useState(undefined);
  const [isSuccess, setIsSuccess] = useState(false);

  const [autoKeywordsGenerationLoading, setAutokeywordGenerationLoading] =
    useState(false);
  const [isAutomaticTextAnalysis, setIsAutomaticTextAnalysis] = useState(false);

  const { t } = useTranslation();

  const history = useHistory();

  const dispatch = useDispatch();

  const { user } = useSelector(UserSessionSelector);
  const {
    textForm,
    orderType,
    assistantId,
    orderContentType,
    orderWebsite,
    textType,
    isLoading,
    isError,
  } = useSelector(adminAnalyzerSelector);

  const formRef = useRef(null);

  const { scheduleSave, isSaving } = useAutosave(autoSaveText, 1000);

  const {
    sidebarWidth,
    setSidebarWidth,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarRef,
    handleOpenSidebarAndScrollTop,
    showSidebar,
  } = useSidebar(textType, orderContentType);

  const { semanticScore } = useSemanticScore();
  const { wordsInBold } = useWordsInBold();

  const {
    // State values
    textTitle,
    keyword,
    url,
    titleTag,
    metaDescriptionTag,
    actualTextContent,
    actualHtmlContent,
    analyseText,
    analyseResult,
    keywords,
    combinedKeywords,
    list,
    stuffedKeywords,
    websites,
    initialStuffedKeywords,
    threadId,
    initialTextContent,
    initialHtmlContent,
    // Setters
    setTextTitle,
    setKeyword,
    setUrl,
    setTitleTag,
    setMetaDescriptionTag,
    setActualTextContent,
    setActualHtmlContent,
    setAnalyseText,
    setAnalyseResult,
    setKeywords,
    setCombinedKeywords,
    setCombinedKeywordsList,
    setList,
    setWebsites,
  } = useAdminTextForm(textForm, isLoading);

  const {
    editorRef,
    stepOneEditorRef,
    editorInitialized,
    stepOneEditorInitialized,
    handleEditorInit,
    handleStepOneEditorInit,
  } = useEditorManagement();

  const { wordCount: initialTextWordCount } = useWordCountTracker(
    stepOneEditorRef,
    stepOneEditorInitialized
  );

  const { wordCount: actualTextWordCount } = useWordCountTracker(
    editorRef,
    editorInitialized
  );

  const { isPremium, hasInitialTextContent, isEmpty } = useTextStatus(
    textForm,
    orderType,
    user.role
  );

  const handleInputChange = (e) => {
    setWebsites(e.target.value);
  };

  useEffect(() => {
    document.title = t('Typix - Détails du texte');
    window.dispatchEvent(new Event('locationchange'));
  }, [t]);

  const { checkAllValidatedAndGenerateBill } = useAdminWriterBilling({
    writerCommandId: textForm?.writerCommandId,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setIsSuccess,
    t,
  });

  useEffect(() => {
    const checkValidForm = () => {
      if (orderType === 'SMART') {
        setDisabledForm(false);
        return;
      }

      const isPremiumMissingFields =
        orderType === 'PREMIUM' && (!titleTag || !metaDescriptionTag);

      const isSmartWithReviewInvalid =
        orderType === 'SMART_WITH_REVIEW' &&
        orderContentType !== 'GMB' &&
        (!titleTag || !metaDescriptionTag);

      const isUrlMissing = textForm?.initialTextContent && !url;

      if (isPremiumMissingFields || isSmartWithReviewInvalid || isUrlMissing) {
        setDisabledForm(true);
      } else {
        setDisabledForm(false);
      }
    };

    checkValidForm();
  }, [
    orderType,
    orderContentType,
    titleTag,
    metaDescriptionTag,
    url,
    textForm,
  ]);

  const { regenerateChatGPTText, startChecking } =
    useAdminRegenerateChatGPTText({
      textId: textForm?.id,
    });

  const { regenerateLoading } = useAdminTextRegenerationStatus({
    textId: textForm?.id,
    startChecking,
    setErrorSuccessMsg,
    setIsSuccess,
    setErrorSuccess,
    isLoading,
    isError,
    t,
  });

  const { putToDraft } = useAdminPutToDraft({
    t,
    actualHtmlContent,
    actualTextContent,
    analyseResult,
    keyword,
    metaDescriptionTag,
    list,
    combinedKeywords,
    websites,
    stuffedKeywords,
    textTitle,
    titleTag,
    url,
    textId: textForm?.id,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setIsSuccess,
    setErrorSuccess,
  });

  const { notifyClient } = useNotifyClientKeywordStuffing({
    textId: textForm?.id,
    setErrorSuccessMsg,
    setIsSuccess,
    setErrorSuccess,
  });

  const { reactivateInitialText } = useAdminReactivateInitialText({
    t,
    textId: textForm?.id,
    textTitle: textForm?.textTitle,
    setErrorSuccessMsg,
    setIsSuccess,
    setErrorSuccess,
  });

  const { verifyText, deliverText, verifyAndDeliverText } = useAdminTextActions(
    {
      textForm,
      setErrorSuccessMsg,
      setErrorSuccessImg,
      setIsSuccess,
      setErrorSuccess,
      t,
      keywords,
      actualHtmlContent,
      actualTextContent,
      analyseResult,
      keyword,
      metaDescriptionTag,
      combinedKeywords,
      textTitle,
      titleTag,
      actualTextWordCount,
      initialTextWordCount,
      url,
      stuffedKeywords,
      websites,
      checkAllValidatedAndGenerateBill,
    }
  );

  const { editText } = useAdminEditText({
    t,
    textType,
    combinedKeywords,
    orderContentType,
    actualHtmlContent,
    actualTextContent,
    analyseResult,
    keyword,
    metaDescriptionTag,
    list,
    websites,
    stuffedKeywords,
    textTitle,
    titleTag,
    url,
    textId: textForm?.id,
    setErrorSuccessMsg,
    setIsSuccess,
    setErrorSuccess,
    setIsFetchLoading,
  });

  const { invalidateText } = useAdminInvalidateText({
    t,
    textId: textForm?.id,
    setErrorSuccessMsg,
    setIsSuccess,
    setErrorSuccess,
    setIsFetchLoading,
  });

  async function validateData(data, { reset }, event) {
    let action = event.nativeEvent.submitter.value;

    // --- NEW LOGIC ---
    let finalHtmlContent = actualHtmlContent; // Start with the current content.

    // If the action requires highlighting, AWAIT the update and capture the result.
    if (action === 'PutToDraft' || action === 'DeliverText') {
      finalHtmlContent = await applyHighlightingAndWait();
    }
    // --- END NEW LOGIC ---

    // Now that we have the guaranteed final content, we create "wrapped" versions
    // of our submission functions that will pass this content along.
    const wrappedPutToDraft = (demande) =>
      putToDraft(demande, finalHtmlContent);
    const wrappedDeliverText = (demande) =>
      deliverText(demande, finalHtmlContent);
    // You may need to wrap keywordStuffingCall as well if it saves content.

    // Call checkData with the original structure, but with our wrapped functions.
    // The execution will only reach here AFTER applyHighlightingAndWait has resolved.

    await checkDataAdmin(
      data,
      orderType,
      orderContentType,
      formRef,
      action,
      wrappedPutToDraft,
      wrappedDeliverText,
      editText,
      invalidateText,
      reactivateInitialText,
      verifyAndDeliverText,
      verifyText
    );
  }

  const handleTextEditorChange = (value, editor) => {
    let textValue = editor.getContent({ format: 'text' });

    if (!textForm.validated) {
      scheduleSave(
        textValue,
        value,
        textForm,
        initialHtmlContent,
        initialTextContent,
        orderType
      );
    }

    if (orderType !== 'PREMIUM') {
      setActualTextContent(value);
      setActualHtmlContent(value);

      setAnalyseText(textValue);
    } else {
      setActualHtmlContent(value);

      setActualTextContent(textValue);
    }

    const content =
      orderType === 'PREMIUM' ? actualHtmlContent : actualTextContent;

    const text = orderType === 'PREMIUM' ? actualTextContent : analyseText;
    const setContent = (value) => {
      if (orderType === 'PREMIUM') {
        setActualHtmlContent(value);
      } else {
        setActualTextContent(value);
      }
    };

    //desactivate auto analyses
    // const lastChar = textValue.trim().slice(-1);
    // if (sentenceSeparators.includes(lastChar)) {
    //   setIsAutomaticTextAnalysis(true);

    //   setTimeout(() => {
    //     fetchAnalysisResult(
    //       text,
    //       textForm?.initialTextContent,
    //       content,
    //       keywords,
    //       language.id,
    //       orderType,
    //       user,
    //       dispatch,
    //       setLoading,
    //       setAnalyseResult,
    //       setContent,
    //       setList,
    //       setIsSuccess,
    //       setErrorSuccess,
    //       setErrorSuccessMsg,
    //       null,
    //       t,
    //       true
    //     );
    //   }, 0);
    // }
  };

  const renderDeliveryButtons = () => {
    return (
      <>
        {/* 'Bon pour livraison' button - always visible */}
        {disabledForm || textForm.delivered ? (
          <button
            title={t('Mettre un texte en brouillon en livré')}
            className="bt-submit-disabled-y"
          >
            {t('Bon pour livraison')}
          </button>
        ) : (
          <button
            title={t('Mettre un texte en brouillon en livré')}
            type="submit"
            className="bt-submit-y"
            name="action"
            value="DeliverText"
            style={{ background: '#43B929', color: 'white' }}
          >
            {t('Bon pour livraison')}
          </button>
        )}

        {/* 'Bon pour Livraison Vérifié' button - conditionally visible */}
        {disabledForm || (textForm.verifiedByAdmin && textForm.delivered) ? (
          <button
            title={t('Mettre un texte en brouillon en livré verifié')}
            className="bt-submit-disabled-y"
          >
            {t('Bon pour Livraison Vérifié')}
          </button>
        ) : !textForm.verifiedByAdmin && !textForm.delivered ? (
          <button
            title={t('Mettre un texte en brouillon en livré verifié')}
            type="submit"
            className="bt-submit-y"
            name="action"
            value="VerifyAndDeliverText"
            style={{ background: '#43B929', color: 'white' }}
          >
            {t('Bon pour Livraison Vérifié')}
          </button>
        ) : (
          !textForm.verifiedByAdmin &&
          textForm.delivered &&
          !textForm.validated && (
            <button
              title={t('Vérifier le texte')}
              type="submit"
              className="bt-submit-y"
              name="action"
              value="VerifyText"
              style={{ background: '#43B929', color: 'white' }}
            >
              {t('Vérifié')}
            </button>
          )
        )}
      </>
    );
  };

  const highlightWordsInHtml = (htmlContent, wordsToHighlight) => {
    if (!wordsToHighlight || wordsToHighlight.length === 0) {
      return htmlContent;
    }

    let highlightedContent = htmlContent;

    let processedContent = '';
    if (orderType === 'PREMIUM') {
      if (/<\/?[a-z][\s\S]*>/i.test(analyseText)) {
        processedContent = entitiesToSpecialChars(htmlContent);
      } else {
        processedContent = he.decode(htmlContent);
      }
    } else {
      processedContent = he.decode(htmlContent);
    }

    let filteredKeywords = wordsToHighlight.filter((keyword) => {
      return keyword.length > 0 && keyword !== '';
    });

    highlightedContent = replaceTextInBold(processedContent, filteredKeywords);

    return highlightedContent;
  };

  const applyHighlightingAndWait = () => {
    return new Promise((resolve) => {
      if (!editorInitialized || !editorRef.current) {
        // If editor isn't ready, resolve immediately with the current state.
        resolve(actualHtmlContent);
        return;
      }

      const editor = editorRef.current;
      const currentRawHtml = editor.getContent();
      const newHighlightedHtml = highlightWordsInHtml(
        currentRawHtml,
        wordsInBold
      );

      if (newHighlightedHtml !== currentRawHtml) {
        editor.setContent(newHighlightedHtml);

        // Update the state and use the callback version of the state setter.
        // The callback runs AFTER the state has been updated.
        setActualHtmlContent(() => {
          // Now that the state is updated, resolve the promise.
          resolve(newHighlightedHtml);
          return newHighlightedHtml; // Return the new value for the state
        });

        // We also update the text content state.
        setActualTextContent(editor.getContent({ format: 'text' }));
      } else {
        // If no changes were made, resolve immediately with the existing content.
        resolve(actualHtmlContent);
      }
    });
  };
  if (isFetchLoading || isLoading || isError) return <Loader />;
  return (
    <WordsProvider>
      <SemanticScoreProvider>
        <div className="content-center">
          <Form
            ref={formRef}
            onSubmit={validateData}
            style={{
              transition: 'margin-right 0.3s ease',
              marginRight: sidebarCollapsed || !showSidebar ? 0 : sidebarWidth,
            }}
          >
            <FormHeader
              textTitle={textTitle}
              onBackClick={() => history.goBack()}
              onDownloadClick={() => DownloadOneTextWordType(textForm.id, user)}
              userRole={ROLES.ADMIN}
              orderType={orderType}
              regenerateLoading={regenerateLoading}
              regenerateChatGPTText={regenerateChatGPTText}
            />

            <TextDetailsSection
              t={t}
              formData={{
                textTitle,
                keyword,
                url,
                titleTag,
                metaDescriptionTag,
              }}
              handlers={{
                setTextTitle,
                setKeyword,
                setUrl,
                setTitleTag,
                setMetaDescriptionTag,
              }}
            />

            {textForm &&
              (textForm.globalRevisionSent || textForm.revisionSent) &&
              textForm.revisionText && (
                <RevisionDetails
                  t={t}
                  textId={textForm.id}
                  revisionRequestAt={textForm.revisionRequestAt}
                  revisionText={textForm.revisionText}
                  revisionFileName={textForm.revisionFileName}
                />
              )}

            {hasInitialTextContent || isPremium ? (
              <div className="row mt-2">
                <InitialTextSection
                  textForm={textForm}
                  handleStepOneEditorInit={handleStepOneEditorInit}
                  textType={textType}
                  userRole={user.role}
                  t={t}
                />
              </div>
            ) : null}

            <div className="row mt-2">
              <div className="col-12">
                <dvi>
                  <div className="label-title label-text-h">
                    <strong>{t('Étape')} 2</strong> -{' '}
                    {t('Ajouter ou compléter ci-dessous votre contenu')} :
                  </div>

                  <div className="box-wrapper-editor">
                    {regenerateLoading && <Loader />}
                    {!textForm && <Loader />}
                    <CustomEditor
                      disabled={regenerateLoading}
                      content={
                        orderType !== 'PREMIUM'
                          ? actualTextContent
                          : actualHtmlContent
                      }
                      onInit={(evt, editor) => handleEditorInit(editor)}
                      onChange={handleTextEditorChange}
                      t={t}
                      threadId={threadId}
                      assistantId={assistantId}
                      orderType={orderType}
                      orderContentType={orderContentType}
                    />
                  </div>
                </dvi>

                <SavingIndicator isSaving={isSaving} />

                <div className="d-flex justify-content-between flex-wrap mt-3">
                  <div style={{ paddingLeft: 10 }}>
                    <LanguageDropdown
                      t={t}
                      language={language}
                      setLanguage={setLanguage}
                    />
                  </div>

                  <div>
                    {!isLoading ? (
                      <WordCountDisplay
                        userRole={user.role}
                        textType={textType}
                        t={t}
                        minWords={textForm?.minWords}
                        maxWords={textForm?.maxWords}
                        actualTextWordCount={actualTextWordCount}
                        initialTextWordCount={initialTextWordCount}
                      />
                    ) : (
                      ''
                    )}

                    <div className="d-flex flex-wrap">
                      <div className="d-inline-flex flex-wrap gap-2">
                        {renderDeliveryButtons()}
                        {initialStuffedKeywords &&
                          initialStuffedKeywords.length > 0 &&
                          !textForm.delivered && (
                            <span>
                              <button
                                title={t(
                                  'Ce bouton est pour informer le client que le texte initial, contenait déjà du keyword stuffing'
                                )}
                                className="bt-submit-y"
                                onClick={notifyClient}
                                style={{
                                  marginLeft: '10px',
                                  background: '#43B929',
                                  color: 'white',
                                }}
                              >
                                {t('Informer client')}
                              </button>
                            </span>
                          )}
                        {textForm?.validated && (
                          <button
                            title={t('Remettre un texte validé à livré')}
                            type="submit"
                            className="bt-submit-y"
                            name="action"
                            value="InvalidateText"
                          >
                            {t('Invalider le texte')}
                          </button>
                        )}

                        {textType !== 'TRANSLATED' && (
                          <>
                            {!isEmpty(textForm.url) &&
                            !isEmpty(textForm.initialTextContent) ? (
                              <span>
                                <button
                                  className="bt-submit-y"
                                  onClick={() => setShowComparison(true)}
                                >
                                  {t('Comparer')}
                                </button>{' '}
                              </span>
                            ) : (
                              ''
                            )}
                          </>
                        )}

                        <span>
                          {textForm.delivered && !textForm.validateAt ? (
                            <button
                              type="submit"
                              className="bt-submit-y"
                              name="action"
                              value="PutToDraft"
                            >
                              {t('Brouillon')}
                            </button>
                          ) : (
                            <button className="bt-submit-disabled-y">
                              {t('Brouillon')}
                            </button>
                          )}
                        </span>

                        <span>
                          {!textForm.validateAt ? (
                            <button
                              type="submit"
                              className="bt-submit-y"
                              name="action"
                              value="EditText"
                            >
                              {t('Editer')}
                            </button>
                          ) : (
                            <button className="bt-submit-disabled-y">
                              {t('Editer')}
                            </button>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>

          {showSidebar && (
            <TextDisplaySidebar
              //score bar status and dimensions
              ref={sidebarRef}
              collapsed={sidebarCollapsed}
              setCollapsed={setSidebarCollapsed}
              width={sidebarWidth}
              setWidth={setSidebarWidth}
              // text keywords(gap)
              combinedKeywords={combinedKeywords}
              autoKeywordsGenerationLoading={autoKeywordsGenerationLoading}
              keywords={keywords}
              //order data
              orderContentType={orderContentType}
              orderWebsite={orderWebsite}
              //text data
              actualHtmlContent={actualHtmlContent}
              actualTextContent={actualTextContent}
              userRole={user.role}
              keyword={keyword}
              list={list}
              language={language.id}
              maxWords={textForm?.maxWords}
              wordCount={actualTextWordCount}
              textId={textForm?.id}
              //internal linking websites
              websites={websites}
              handleKeywordChange={(e) => setCombinedKeywords(e.target.value)}
              handleInputChange={handleInputChange}
              t={t}
            />
          )}

          <ComparisonModal
            open={showComparison}
            initialText={textForm.initialTextContent}
            text={he.decode(actualTextContent)}
            handleClose={() => setShowComparison(false)}
          />
          {errorSuccessMsg && (
            <ErrorSuccesModal
              open={errorSuccess}
              message={errorSuccessMsg}
              handleClose={() => setErrorSuccess(false)}
              success={isSuccess}
              imageUrl={errorSuccessImg}
            />
          )}
        </div>
      </SemanticScoreProvider>
    </WordsProvider>
  );
}

export default function TextDisplay() {
  return (
    <WordsProvider>
      <SemanticScoreProvider>
        <TextDisplayContent />
      </SemanticScoreProvider>
    </WordsProvider>
  );
}
